// Print/PDF generation thunks extracted from Services/ServicesThunks.js

import _ from "lodash";
import axios from "lib/axiosInstance";
import moment from "moment";
import { generateRandomId, getBackendServerUrl } from "Utils";
import {
  setToastMsg,
  getUserInfo,
  logBulkActionActivity,
} from "Login/modules/LoginModule";
import { isNewPYPConceptsLabelEnabled } from "lib/i18nHelperVariables";
import {
  downloadFamilyInvitesInBulkMutation,
  generateBulkPdfZipFileMutation,
} from "modules/CommonMutations";
import client from "lib/apolloClient/client";
import ACLStore from "lib/aclStore";
import { Sentry } from "services/sentry";
import { ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE } from "Constants/permissionConstants";
import { getExtensions } from "services/tracing";
import {
  setProgress,
  removeProgressElement,
} from "modules/Services/ServicesActions";
import {
  getIsCourseBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import { getSettingValue } from "Utils/ConfigurationUtils";
import { getSelectedCourseId } from "modules/CommonSelectors";
import { addApiEvent, downloadFromUrl } from "modules/Services";

export const getPrintFile = ({
  id,
  mode = "pdfurl",
  shouldOpenInWindow = true,
  type,
  getIds,
  extraParams = [],
  metadata = {},
  isDownload = false,
  name = "",
  throwErrorEnabled = false,
  lng,
  portalType,
  curriculumProgram,
}) => {
  return async (dispatch, getState) => {
    let userInfo = getUserInfo({ portalType });
    const userId = userInfo.id;
    const userType = userInfo.user_type;

    // to print sample units, we need JWT of sample org
    const sampleOrgPortalType = getState().platform.sampleOrgPortalType;
    if (!_.isEmpty(sampleOrgPortalType)) {
      userInfo = getUserInfo({ portalType: sampleOrgPortalType });
    }

    const extensions = getExtensions({
      request: {},
    });

    metadata.userId = _.get(userInfo, "id", "");
    metadata.organizationId = _.get(userInfo, "org_id", "");
    metadata.userType = userType;
    metadata.extensions = extensions;
    if (userType !== "staff") {
      metadata.studentIds =
        userType == "parent" ? [userInfo.childID] : [userId];
    }
    if (userType === "parent") {
      metadata.studentId = userInfo.childID;
    }
    if (userType === "student") {
      metadata.studentId = userId;
    }

    if (_.includes(["progressreport"], type)) {
      let curriculumProgramIdFromArgument = null,
        curriculumProgramTypeFromArgument = null,
        curriculumProgramIdFromRedux = null,
        curriculumProgramTypeFromRedux = null;
      if (!_.isNil(curriculumProgram)) {
        metadata.curriculumProgramId = curriculumProgram.id;
        curriculumProgramIdFromArgument = curriculumProgram.id;
        metadata.curriculumProgramType = curriculumProgram.type;
        curriculumProgramTypeFromArgument = curriculumProgram.type;
      } else {
        const state = getState();
        const curriculumProgramId = _.get(
          state,
          `platform.currentCurriculumProgram.id`
        );
        curriculumProgramIdFromRedux = curriculumProgramId;
        const curriculumProgramType = _.get(
          state,
          `platform.currentCurriculumProgram.type`
        );
        curriculumProgramTypeFromRedux = curriculumProgramType;
        metadata.curriculumProgramId = curriculumProgramId;
        metadata.curriculumProgramType = curriculumProgramType;
      }
      if (
        _.isNil(metadata.curriculumProgramId) ||
        _.isNil(metadata.curriculumProgramType)
      ) {
        Sentry.captureMessage("NO_CURRICULUM_INFO_FOUND", {
          extra: {
            curriculumProgramIdFromArgument,
            curriculumProgramTypeFromArgument,
            curriculumProgramIdFromRedux,
            curriculumProgramTypeFromRedux,
            id,
          },
        });
      }
    }

    // Adding utcOffset to metadata
    // Use case : utcOffset is passed to pdf-gen to convert date to user's timezone, otherwise it will be converted to server's timezone
    const utcOffset = moment().utcOffset();
    metadata.utcOffset = utcOffset;

    /**
     * When we print unit plan, we need to pass isDynamicTemplateLocalisationEnabled.
     * Currently, we are not enabling it for unit plans of sample orgs.
     */
    if (type === "unitplan") {
      const state = getState();
      metadata.isDynamicTemplateLocalisationEnabled =
        ACLStore.can(ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE) &&
        !sampleOrgPortalType;
      const isLearningCourseEnabled = getIsLearningCoursesEnabled();

      if (isLearningCourseEnabled) {
        metadata.isLearningCourseEnabled = isLearningCourseEnabled;
        metadata.learningCourseId = getSelectedLearningCourseId(state);
        metadata.isCourseBasedGrading = getIsCourseBasedGradingEnabled();
        metadata.courseId = getSelectedCourseId(state);
      }
    }

    // this feature flag is used for renaming key and related key in PYP
    metadata.isNewPYPConceptsLabelEnabled = ACLStore.can(
      "FeatureFlag:NewPYPConceptsLabelEnabled"
    );

    // const user_type = getState().login.userInfo.user_type;
    const locale =
      getSettingValue({ name: "locale", readFromUserAndOrgLevel: true }) ||
      "en";
    const token = _.get(userInfo, "token", "");
    const fileId = generateRandomId();
    let result;
    let winRef = null;

    // eslint-disable-next-line no-restricted-properties
    const windowOpen = window.open;

    if (mode == "zipurl") {
      isDownload = true;
    }

    // Add api event when generating unit plan pdf
    if (type == "unitplan")
      dispatch(addApiEvent({ eventType: "GENERATE_UNIT_PLAN_PDF" }));

    try {
      const attachment = {
        id: fileId,
        type: "FILE",
        name,
        mimeType: mode == "zipurl" ? "application/zip" : "application/pdf",
      };

      if (shouldOpenInWindow && !isDownload) {
        winRef = windowOpen("", "_blank");
        const loadingHTML = `<html style="height:100%"><body style="height:100%">
      <div style="height:100%;display:flex;flex:1;align-item:center;justify-content:center">
      <img style="width:40px" src="https://cloud.toddleapp.com/assets/webapp/loading.svg"/>
      </div></body></html>`;

        winRef.document.write(loadingHTML);
      }
      if (isDownload) {
        dispatch(
          setProgress({
            id: fileId,
            progress: 0,
            attachment,
            status: "SAVE",
            type: "FILE",
          })
        );
      }
      if (getIds) {
        const ids = await getIds();
        id = _.join(ids, ",");
      }

      const params = {
        mode,
        type,
        id,
        token,
        lng: lng ? lng : locale,
        metadata: encodeURIComponent(JSON.stringify(metadata)),
        ...extraParams,
      };

      const queryString = _.join(
        _.map(params, (val, key) => `${key}=${val}`),
        "&"
      );
      const url = getBackendServerUrl({
        path: `/pdfs?${queryString}`,
        token,
      });
      result = await axios.get(url);

      const { data } = result || {};

      /**
       * HACK: download or print preview sample pdf for myp reports
       */
      // if (
      //   curriculumProgramType === CURRICULUM_TYPE_MYP &&
      //   (type === "progressreport" || type === "progress_report_preview")
      // ) {
      //   data.fileUrl = "https://cloud.toddleapp.com/s/content/rkPO5RNBu.pdf"; // sample pdf url
      // }

      if (data.fileUrl) {
        if (winRef) {
          winRef.location = data.fileUrl;
        } else {
          if (isDownload) {
            attachment.url = data.fileUrl;

            if (_.isEmpty(attachment.name)) {
              attachment.name = data.fileName;
            }

            dispatch(downloadFromUrl({ attachment, shouldAskForSave: true }));
          }
          return data.fileUrl;
        }
      } else {
        if (throwErrorEnabled)
          throw new Error("Failed to recieve data in getPrintFile");
        dispatch(removeProgressElement({ id: fileId }));
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    } catch (e) {
      if (winRef) {
        winRef?.close();
      }
      dispatch(removeProgressElement({ id: fileId }));

      if (e.response) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      } else if (e.networkError) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:no_internet_connection",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }

      if (throwErrorEnabled) throw e;
    }
  };
};

export const printFamilyInviteInBulk = ({
  entityIds,
  selectedLanguage,
  entityType,
  name,
  isChinaTemplate = false,
  activityLogModule = null,
}) => {
  return async dispatch => {
    const userInfo = getUserInfo();
    const fileId = generateRandomId();
    const pdfLanguage = _.toUpper(selectedLanguage);
    const isNewPYPConceptsLabel = isNewPYPConceptsLabelEnabled();
    const utcOffset = String(moment().utcOffset());

    try {
      const attachment = {
        id: fileId,
        type: "FILE",
        name,
        mimeType: "application/zip",
      };

      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );

      const response = await client.mutate({
        mutation: downloadFamilyInvitesInBulkMutation,
        variables: {
          input: {
            entityIds,
            entityType,
            isNewPYPConceptsLabelEnabled: isNewPYPConceptsLabel,
            pdfLanguage,
            utcOffset,
            isChinaTemplate,
          },
        },
      });

      const data = _.get(
        response,
        "data.platform.downloadFamilyInvitesInBulk",
        {}
      );
      const isSuccess = _.get(data, "isSuccess", false);
      const fileUrl = _.get(data, "fileUrl", "");
      const message = _.get(data, "message", "");

      if (isSuccess) {
        if (fileUrl) {
          attachment.url = fileUrl;
          dispatch(downloadFromUrl({ attachment, shouldAskForSave: true }));
          dispatch(
            setToastMsg({
              msg: "toastMsgs:family_invites_have_been_downloaded",
              type: "success",
            })
          );
        } else if (message === "PDFS_WILL_BE_SHARED_VIA_EMAIL") {
          dispatch(
            setToastMsg({
              msg: "toastMsgs:send_email_when_files_are_ready_for_download",
              locale_params: [
                {
                  key: "email",
                  value: _.get(userInfo, "email", "user"),
                  isPlainText: true,
                },
              ],
              type: "success",
            })
          );
        }
        if (activityLogModule)
          logBulkActionActivity({
            input: {
              operation: "READ",
              module: activityLogModule,
              activityType: "BULK_ACTION",
              activityMessage: "DOWNLOADED_BULK_FAMILY_INVITES",
              description: `Downloaded ${entityIds.length} ${entityType} bulk family invites.`,
              entityType: entityType,
              metadata: {
                entityType,
                entityIds,
                count: entityIds.length,
              },
            },
          });
      } else {
        throw new Error(message);
      }

      dispatch(removeProgressElement({ id: fileId }));
    } catch (error) {
      dispatch(removeProgressElement({ id: fileId }));
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
    }
  };
};

export const generatePdfZipInBulk = ({
  filterIds,
  selectedLanguage,
  filterType,
  name,
  pdfType,
  toastMsg,
  isChinaTemplate = false,
  activityLogModule = null,
}) => {
  return async dispatch => {
    const userInfo = getUserInfo();
    const fileId = generateRandomId();
    const pdfLanguage = _.toUpper(selectedLanguage);
    const isNewPYPConceptsLabel = isNewPYPConceptsLabelEnabled();
    const utcOffset = String(moment().utcOffset());

    try {
      const attachment = {
        id: fileId,
        type: "FILE",
        name,
        mimeType: "application/zip",
      };

      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );

      const response = await client.mutate({
        mutation: generateBulkPdfZipFileMutation,
        variables: {
          input: {
            filterIds,
            filterType,
            isNewPYPConceptsLabelEnabled: isNewPYPConceptsLabel,
            utcOffset,
            pdfLanguage,
            pdfType,
            isChinaTemplate,
          },
        },
      });

      const data = _.get(response, "data.platform.generateBulkPdfZipFile", {});
      const isSuccess = _.get(data, "isSuccess", false);
      const fileUrl = _.get(data, "fileUrl", "");
      const message = _.get(data, "message", "");

      if (isSuccess) {
        if (fileUrl) {
          attachment.url = fileUrl;
          dispatch(downloadFromUrl({ attachment, shouldAskForSave: true }));
          dispatch(
            setToastMsg({
              msg: toastMsg || "toastMsg:downloaded_successfully",
              type: "success",
            })
          );
        } else if (message === "PDFS_WILL_BE_SHARED_VIA_EMAIL") {
          dispatch(
            setToastMsg({
              msg: "toastMsgs:send_email_when_files_are_ready_for_download",
              locale_params: [
                {
                  key: "email",
                  value: _.get(userInfo, "email", "user"),
                  isPlainText: true,
                },
              ],
              type: "success",
            })
          );
        }
        if (activityLogModule)
          logBulkActionActivity({
            input: {
              operation: "READ",
              module: activityLogModule,
              activityType: "BULK_ACTION",
              activityMessage: `DOWNLOADED_${pdfType}`,
              description: `Downloaded ${
                filterIds?.length ?? 0
              } ${pdfType} files.`,
              entityType: filterType,
              metadata: {
                entityType: filterType,
                entityIds: filterIds,
                count: filterIds?.length ?? 0,
              },
            },
          });
      } else {
        throw new Error(message);
      }

      dispatch(removeProgressElement({ id: fileId }));
    } catch (error) {
      dispatch(removeProgressElement({ id: fileId }));
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
    }
  };
};
